import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { codigoValidacaoService } from "../services/codigoValidacaoService";
import { franquiaService } from "../services/franquiaService";
import { CodigoValidacao } from "../types/codigoValidacao";

// --- Helper Components ---

const LoadingSpinner: React.FC<{ message: string }> = ({ message }) => (
  <div className="flex flex-col items-center justify-center text-center">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    <p className="mt-4 text-lg text-gray-600">{message}</p>
  </div>
);

const ErrorDisplay: React.FC<{
  title: string;
  message: string;
  showRetry?: boolean;
  onRetry?: () => void;
}> = ({ title, message, showRetry, onRetry }) => (
  <div className="text-center bg-red-50 p-8 rounded-lg shadow-md">
    <h2 className="text-2xl font-bold text-red-700">{title}</h2>
    <p className="mt-4 text-gray-600 whitespace-pre-wrap break-words">
      {message}
    </p>
    <div className="mt-6 flex gap-4 justify-center">
      {showRetry && onRetry && (
        <button
          onClick={onRetry}
          className="bg-yellow-600 text-white font-bold py-2 px-4 rounded hover:bg-yellow-700"
        >
          Tentar Novamente
        </button>
      )}
      <Link
        to="/login"
        className="bg-blue-600 text-white font-bold py-2 px-4 rounded hover:bg-blue-700"
      >
        Voltar para o Login
      </Link>
    </div>
  </div>
);

// --- Main Component ---

type FormData = {
  email: string;
  razao_social: string;
  nome_fantasia: string;
  cnpj: string;
  inscricao_municipal: string;
  inscricao_estadual: string;
  admin_nome_completo: string;
  admin_cpf: string;
  admin_password: string;
  password_confirm: string;
};

export const Register: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [step, setStep] = useState<
    "verifying-code" | "email-form" | "register-form" | "error"
  >("verifying-code");
  const [validationCode, setValidationCode] = useState<string | null>(null);
  const [inviteData, setInviteData] = useState<CodigoValidacao | null>(null);
  const [errorMessage, setErrorMessage] = useState({ title: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormData>();

  useEffect(() => {
    const code = searchParams.get("codigo");
    if (!code) {
      setErrorMessage({
        title: "Convite Necessário",
        message:
          "Para se registrar, você precisa de um link de convite. Se você não tem um, por favor, entre em contato conosco.",
      });
      setStep("error");
      return;
    }
    setValidationCode(code);

    const verifyCode = async () => {
      try {
        console.log("[Register] Verificando código:", code);
        await codigoValidacaoService.verificarCodigo(code);
        console.log("[Register] Código verificado com sucesso");
        setStep("email-form");
      } catch (error: any) {
        console.error("[Register] Erro ao verificar código:", error);
        const errorMsg =
          error.response?.data?.detail ||
          error.message ||
          "Este link de convite é inválido, expirou ou já foi utilizado. Por favor, solicite um novo convite ou entre em contato conosco.";
        setErrorMessage({ title: "Convite Inválido", message: errorMsg });
        setStep("error");
      }
    };

    verifyCode();
  }, [searchParams]);

  const onEmailSubmit = async (data: FormData) => {
    if (!validationCode) return;
    setLoading(true);
    try {
      console.log("[Register] Verificando email:", validationCode, data.email);
      const response = await codigoValidacaoService.verificarEmail(
        validationCode,
        data.email
      );
      console.log("[Register] Email verificado com sucesso:", response);
      setInviteData(response);
      setStep("register-form");
    } catch (error: any) {
      console.error("[Register] Erro ao verificar email:", error);
      const apiError =
        error.response?.data?.detail ||
        error.message ||
        "Ocorreu um erro ao verificar seu e-mail. Tente novamente.";
      setErrorMessage({ title: "Erro na Verificação", message: apiError });
      setStep("error");
    } finally {
      setLoading(false);
    }
  };

  const onRegisterSubmit = async (data: any) => {
    if (!validationCode || !inviteData) return;
    setLoading(true);

    const registrationData = {
      ...data,
      codigo_validacao: validationCode,
      admin_email: inviteData.email_convidado, // Ensure the correct email is sent
    };

    try {
      console.log(
        "[Register] Iniciando registro de franquia:",
        registrationData
      );
      await franquiaService.registrarFranquia(registrationData);
      console.log("[Register] Registro concluído com sucesso");
      navigate("/login", {
        replace: true,
        state: {
          message:
            "Cadastro da franquia realizado com sucesso! Faça login para continuar.",
        },
      });
    } catch (error: any) {
      console.error("[Register] Erro ao registrar franquia:", error);
      const apiError =
        error.response?.data?.detail ||
        error.message ||
        "Ocorreu um erro ao finalizar o cadastro. Por favor, verifique seus dados e tente novamente.";
      setErrorMessage({ title: "Erro no Cadastro", message: apiError });
      setStep("error");
    } finally {
      setLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case "verifying-code":
        return <LoadingSpinner message="Verificando convite..." />;

      case "email-form":
        return (
          <form onSubmit={handleSubmit(onEmailSubmit)} className="space-y-6">
            <div>
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-700"
              >
                Confirme seu E-mail
              </label>
              <input
                id="email"
                type="email"
                {...register("email", { required: "E-mail é obrigatório" })}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.email.message as string}
                </p>
              )}
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? "Verificando..." : "Continuar"}
            </button>
          </form>
        );

      case "register-form":
        return (
          <form onSubmit={handleSubmit(onRegisterSubmit)} className="space-y-4">
            <h3 className="text-lg font-medium text-gray-800 border-b pb-2">
              Dados da Franquia
            </h3>
            <input
              {...register("razao_social", { required: true })}
              placeholder="Razão Social"
              className="w-full p-2 border rounded"
            />
            <input
              {...register("nome_fantasia")}
              placeholder="Nome Fantasia (Opcional)"
              className="w-full p-2 border rounded"
            />
            <input
              {...register("cnpj", { required: true })}
              placeholder="CNPJ"
              className="w-full p-2 border rounded"
            />
            <input
              {...register("inscricao_municipal")}
              placeholder="Inscrição Municipal"
              className="w-full p-2 border rounded"
            />
            <input
              {...register("inscricao_estadual")}
              placeholder="Inscrição Estadual"
              className="w-full p-2 border rounded"
            />

            <h3 className="text-lg font-medium text-gray-800 border-b pb-2 pt-4">
              Seus Dados de Administrador
            </h3>
            <input
              {...register("admin_nome_completo", { required: true })}
              placeholder="Nome Completo"
              className="w-full p-2 border rounded"
            />
            <input
              type="email"
              value={inviteData?.email_convidado}
              readOnly
              className="w-full p-2 border rounded bg-gray-100"
            />
            <input
              {...register("admin_cpf", { required: true })}
              placeholder="CPF"
              className="w-full p-2 border rounded"
            />
            <input
              type="password"
              {...register("admin_password", { required: true, minLength: 6 })}
              placeholder="Senha (mínimo 6 caracteres)"
              className="w-full p-2 border rounded"
            />
            <input
              type="password"
              {...register("password_confirm", {
                validate: (val) =>
                  val === watch("admin_password") || "As senhas não coincidem",
              })}
              placeholder="Confirmar Senha"
              className="w-full p-2 border rounded"
            />
            {errors.password_confirm && (
              <p className="text-sm text-red-600">
                {errors.password_confirm.message as string}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full flex justify-center py-3 px-4 mt-6 border border-transparent rounded-md shadow-sm text-lg font-medium text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
            >
              {loading ? "Finalizando Cadastro..." : "Finalizar Cadastro"}
            </button>
          </form>
        );

      case "error":
        return (
          <ErrorDisplay
            title={errorMessage.title}
            message={errorMessage.message}
            showRetry={retryCount < 3 && step === "error"}
            onRetry={() => {
              if (validationCode) {
                setStep("verifying-code");
                setRetryCount((prev) => prev + 1);
              }
            }}
          />
        );
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 py-12 px-4">
      <div className="max-w-lg w-full bg-white p-8 rounded-xl shadow-lg space-y-6">
        <div className="text-center">
          <img
            src="/TACTGEO_Logo.png"
            alt="Logo Tact IMOBI"
            className="mx-auto h-12 w-auto"
          />
          <h2 className="mt-4 text-2xl font-bold text-gray-800">
            Registro de Nova Franquia
          </h2>
          {step === "email-form" && (
            <p className="mt-2 text-sm text-gray-600">
              Convite validado! Por favor, confirme seu e-mail para continuar.
            </p>
          )}
          {step === "register-form" && (
            <p className="mt-2 text-sm text-gray-600">
              E-mail verificado! Agora, preencha os dados para finalizar seu
              cadastro.
            </p>
          )}
        </div>
        {renderStep()}
      </div>
    </div>
  );
};
